import { useEffect, useState, useRef } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { get } from 'lodash'

import GratuityHeader from '../components/GratuityHeader'
import styles from './CardReader.module.scss'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'

import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import useSpinner from '../context/hooks/useSpinner'

import { getBridgePaymentMethod, getPaymentType } from '../selectors/peripheral'
import { getIsSyncing } from '../VNSyncManager/Selectors'
import { getRemoteOrderTotal } from '../selectors/orderTotalRemote'

import { cardReaderApproved, getCardDataMessage } from '../utils/orderUtils'
import { invoiceNumber } from '../utils/orderNumbers'
import PeripheralBridge from '../utils/peripheralBridge'
import createDataDogLog from '../utils/datadog'
import { ToastManager } from '../utils/toastManager'
import { PAYMENT_TYPES } from '../utils/paymentTypes'
import { getTransactionBrandingConfig } from '../utils/buildSettings'
import { orderUuidFromOrderNumber } from '../utils/orderNumbers'

import { resetSafFullResponse, setBridgePaymentMethod } from '../actions/peripheral'

import { bridgePaymentMethods } from '../reducers/peripheral'
import VNLoader from '../VNLoader/components/VNLoader'
import { CardReaderBusy, CardReaderErrorModal, CardReaderReady } from './CardReader'
import { useSplitTab } from '../VNSplitTabs/hooks/useSplitTab'
import { setStartATabModal } from '../VNDialogs/ActionCreators'
import { orderDetailsRequested, selectOrder } from '../actions/order'
import { getDeviceSerial } from '../utils/deviceData'
import { getNetworkAvailable } from '../selectors/config'

/**
 * Bare bones card reader for auths with only order totals
 * @returns
 */
const CardReaderAuth = ({
  bridgeMethod,
  total,
  deviceType,
  isSyncing,
  message,
  clearSafResults,
  paymentId,
  tenderAmountInCents,
  offlineTotal,
  history,
  className,
  goPrevScreen,
  redirectPath,
  redirectAction,
  authTabs,
  orderNumber,
  splitTab,
  orderDetails,
}) => {
  const classNames = cn(styles.cardReader, className)
  const dispatch = useDispatch()

  const [status, setStatus] = useState('ready') // ready, error, busy
  const [isFreeAuthTab, setIsFreeAuthTab] = useState(false) // free tabs should auth to one dollar
  const [errorMessage, setErrorMessage] = useState(message ?? '')
  const [uuid] = useState(orderUuidFromOrderNumber(orderNumber))

  const [transactionApproved, setTransactionApproved] = useState(false)
  const [transactionError, setTransactionError] = useState(false)
  const [cardReaderInitialized, setCardReaderInitialized] = useState(false)
  const [transactionCancelled, setTransactionCancelled] = useState(false)
  const transactionCancelledRef = useRef(transactionCancelled)
  const isMobile = useGetIsMobileScreen()
  const { startSpinner } = useSpinner()
  const { initiateTab } = useSplitTab()
  const isNetworkAvailable = useSelector(getNetworkAvailable)

  // If there is a redirect path, we handle it as a tabbed order
  const redirect = (data, orderNumber) => {
    if (authTabs) {
      startSpinner()
      if (splitTab) {
        if (isNetworkAvailable && orderDetails?.oldOrder?.wasCreatedInStadium) {
          dispatch(orderDetailsRequested(orderDetails?.oldOrder?.uuid))
        }
        dispatch(selectOrder(orderDetails?.oldOrder?.uuid))
        dispatch(
          setStartATabModal({
            open: true,
            onSaveClick: (tabName) => initiateTab(orderDetails, tabName, {}, data, paymentId, null, true),
            initialInputValue: data?.cardholderName?.trim() || '',
          }),
        )
      }
      history.push(redirectPath, {
        cardReaderData: data,
        paymentId,
        paymentType: PAYMENT_TYPES.CREDIT_CARD,
        orderNumber,
      })
      return
    }

    startSpinner()
    history.push(redirectPath)
  }

  // The single entry point for bridge registration on mount.
  const registerBridgeHandlers = (orderTotal) => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      createDataDogLog('error', 'Card Reader - No Bridge Instance', { uuid })
      return
    }

    createDataDogLog('info', 'Card Reader - Registering Bridge Handlers', { uuid, totalInCents: orderTotal })
    // changes to handleResult need to also be implemented in:
    //   src/VNTransactions/actions/VNTransactionRecovery.js
    bridgeInstance.registerHandler('handleResult', function (data, callback) {
      // If handle result returns null or undefined, set error and abort transaction
      if (!data) {
        setStatus('error')
        setErrorMessage('Bad response.')
        createDataDogLog('error', 'Card Reader - Bad Response', { data, uuid, paymentId })
        return
      }

      // if true, abort due to transaction cancelled
      if (transactionCancelledRef.current) return

      setCardReaderInitialized(false)

      const dataWithUuid = { ...data, orderNumber, uuid }

      if (cardReaderApproved(data)) {
        setTransactionApproved(true)

        redirect(dataWithUuid, null, 0, tenderAmountInCents, tenderAmountInCents, orderNumber, null)
      } else {
        const message = getCardDataMessage(data)

        setStatus('error')
        setErrorMessage(message)

        createDataDogLog('error', 'Card Reader Auth Error - Card Reader Error', {
          data,
          uuid,
          paymentId,
          invoiceNumber: data?.invoiceNumber,
        })
      }

      var response = { message: data }
      callback(response)
    })

    bridgeInstance.registerHandler('errorTransaction', function (data, callback) {
      // if true, abort due to transaction cancelled
      if (transactionCancelledRef.current) return

      createDataDogLog('error', 'Card Reader - Error Transaction Event', { data, uuid })
      setCardReaderInitialized(false)

      const dataWithUuid = { ...data, uuid }
      const response = { message: dataWithUuid }

      setStatus('error')
      setTransactionError(true)

      callback(response)
    })

    bridgeInstance.registerHandler('onTransactionRequestStarted', function (data, callback) {
      createDataDogLog('info', 'Card Reader - Transaction Request Started', { data, uuid })
    })
  }
  /**
   * Initiate the card reader with a transaction for a specific amount.
   * Tabbed orders, when
   *
   * @param {String} totalAsString
   * @returns
   */
  const initiateCardReader = (totalAsString, fromCallback = true) => {
    if (fromCallback && status === 'error') return

    let orderTotal = totalAsString
    // free tabs should auth to one dollar
    if (authTabs && totalAsString === '0.00') {
      setIsFreeAuthTab(true)
      orderTotal = '1.00'
    }

    // Don't initiate the card reader if the order is free.
    if (orderTotal === '0.00') return

    // Synchronously register the bridge handlers when the card reader is known to be being
    // initialized. This prevents erroneous bridge callbacks from being made by the APK before the
    // card reader is actually ready. (e.g. a cancellation attempt before a transaction has
    // actually started)
    registerBridgeHandlers(orderTotal)

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (!orderTotal) {
      ToastManager.error('Reader initialization failed : Invalid total. Please go back and try your transaction again.', { autoClose: false })
      return
    }

    const deviceSerial = getDeviceSerial()
    setCardReaderInitialized(true)

    // contract should match com/venuenext/poscore/model/Transaction.kt
    bridgeInstance.call(bridgeMethod, {
      uuid: uuid,
      orderNumber,
      hRef: invoiceNumber(orderNumber),
      amount: orderTotal,
      tipAmount: 0,
      ...getTransactionBrandingConfig(),
      deviceSerial: deviceSerial,
    })

    createDataDogLog('info', 'Card Reader - Execute Sale Request Made', {
      orderUuid: uuid,
      orderNumber,
      invoiceNumber: invoiceNumber(orderNumber),
      transactionAmount: orderTotal,
    })
  }

  const showProcessing = isMobile && deviceType === 'pax'
  const totalInCents = total || offlineTotal
  const totalDisplay = `$${(totalInCents / 100).toFixed(2)}`
  const grandTotalInCents = totalInCents
  const formattedTotal = (grandTotalInCents / 100).toFixed(2)
  const noPaymentRequired = totalInCents === 0

  let primaryText = totalDisplay ?? '-'

  const onCancel = () => {
    setTransactionCancelled(true)
    goPrevScreen()
  }

  const onGoBack = () => {
    onCancel(false, 'User cancelled order by selecting Go Back')
  }

  const onRetry = () => {
    createDataDogLog('info', 'Card Reader Auth - Retry', {
      uuid,
      orderNumber,
      invoiceNumber: invoiceNumber(orderNumber),
      amountInCents: totalInCents,
    })

    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance || transactionError) {
      setTransactionError(false)
      setStatus('ready')
      initiateCardReader(formattedTotal, false)
      return
    }

    setStatus('ready')
    setErrorMessage()
    initiateCardReader(formattedTotal, false)
  }

  const shutDownCardReader = () => {
    createDataDogLog('info', 'Card Reader Auth - Shut Down', {
      uuid,
      orderNumber,
      invoiceNumber: invoiceNumber(orderNumber),
      amountInCents: totalInCents,
    })

    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance) bridgeInstance.call('cancelTransactionAttempt')
  }

  useEffect(() => {
    clearSafResults()
    registerBridgeHandlers()
    initiateCardReader(formattedTotal, false)

    const handleBeforeUnload = (event) => {
      createDataDogLog('error', 'Card Reader Auth - Screen Reloaded Unexpectedly', {
        uuid,
        orderNumber,
        invoiceNumber: invoiceNumber(orderNumber),
        amountInCents: totalInCents,
        bridgeActive: cardReaderInitialized,
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeSaleRequest))
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (transactionError) {
      setStatus('error')
    }
  }, [transactionError])

  useEffect(() => {
    transactionCancelledRef.current = transactionCancelled
  }, [transactionCancelled])

  let content = showProcessing ? <VNLoader standalone /> : <CardReaderReady deviceType={deviceType} safIsUploading={isSyncing} />

  if (status === 'error') {
    content = (
      <CardReaderErrorModal
        message={errorMessage}
        onCancel={() => onCancel(true, 'User cancelled order transaction from card reader ERROR modal')}
        onRetry={onRetry}
        deviceType={deviceType}
      />
    )
  }

  if (status === 'busy') {
    content = <CardReaderBusy onCancel={() => onCancel(true, 'User cancelled order transaction from card reader BUSY modal')} />
  }

  useEffect(() => {
    return () => {
      if (!transactionApproved) {
        shutDownCardReader()
      }
    }
    // eslint-disable-next-line
  }, [transactionApproved])

  if (noPaymentRequired && !isFreeAuthTab) {
    return <VNLoader />
  }

  return (
    <div className={cn(classNames)}>
      <VNConcessionsNavBar
        textDisplay={isMobile && primaryText}
        onClick={onGoBack}
        cardReaderScreen={true}
        cardReaderInitialized={cardReaderInitialized}
      />
      <GratuityHeader primaryText={!isMobile && primaryText} />
      <div className={styles.contentContainer}>{content}</div>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const total = ownProps?.history?.location?.state?.total
  const redirectPath = ownProps?.history?.location?.state?.redirectPath ?? ''
  const authTabs = ownProps?.history?.location?.state?.authTabs ?? ''
  const offlineTotal = ownProps?.history?.location?.state?.offlineTotal
  const paymentId = ownProps?.history?.location?.state?.paymentId
  let tenderAmountInCents = ownProps?.history?.location?.state?.tenderAmountInCents ?? 0
  const message = ownProps?.history?.location?.state?.message
  const orderNumber = ownProps?.history?.location?.state?.orderNumber
  const redirectAction = ownProps?.history?.location?.state?.redirectAction
  const splitTab = ownProps?.history?.location?.state?.splitTab
  const orderDetails = ownProps?.history?.location?.state?.orderDetails
  const remoteOrderTotal = getRemoteOrderTotal(state)
  const orderUuid = remoteOrderTotal?.uuid

  const bridgeMethod = getBridgePaymentMethod(state)
  const deviceType = getPaymentType(state)
  const isSyncing = getIsSyncing(state)

  // [T36260] free tabs should auth to one dollar
  if (authTabs && tenderAmountInCents === 0) {
    tenderAmountInCents = 100
  }

  return {
    offlineTotal,
    redirectAction,
    paymentId,
    orderUuid,
    tenderAmountInCents,
    message,
    bridgeMethod,
    deviceType,
    isSyncing,
    total,
    redirectPath,
    authTabs,
    orderNumber,
    splitTab,
    orderDetails,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const goPrevScreen = get(ownProps, 'history.goBack', () => {})

  return {
    goPrevScreen: () => goPrevScreen(),
    onClose: () => ownProps?.history?.location?.state?.onClose,
    clearSafResults: () => {
      // Clear the SAF results so that the next time the user goes to the card reader screen, the
      // results are not stale.
      dispatch(resetSafFullResponse())
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CardReaderAuth))
